import { PREVIOUS_USER_KEY, TOKEN_KEY, USER_KEY } from "@/constants";
import { messages } from "@/messages";
import { IAxiosOnErrorResponse } from "@/types";
import axios, { AxiosResponse } from "axios";

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Authorization: "Bearer " + window.localStorage.getItem(TOKEN_KEY),
  },
});

export function setAuthHeader(token: string) {
  client.defaults.headers["Authorization"] = `Bearer ${token}`;
}

const onSuccess = function (response: AxiosResponse<any, any>) {
  return response.data;
};

type ErrorBody = {
  message?: unknown;
  errors?: Array<{ param?: string; msg?: string }>;
};

/**
 * Server message, else the first validation error, else undefined (e.g. an
 * HTML 502) so each caller keeps its own fallback copy.
 */
function errorMessage(data: unknown): string | undefined {
  if (data && typeof data === "object") {
    const body = data as ErrorBody;
    if (typeof body.message === "string" && body.message) return body.message;
    const first = Array.isArray(body.errors) ? body.errors[0] : undefined;
    if (first?.param || first?.msg) {
      return `${first.param ?? ""} ${first.msg ?? ""}`.trim();
    }
  }
  return undefined;
}

const onError = function (error: any): Promise<IAxiosOnErrorResponse> {
  // Method, URL and status only: request bodies carry passwords, Google ID
  // tokens and signup tickets, and headers carry the bearer token.
  const method = error?.config?.method?.toUpperCase() ?? "?";
  const url = error?.config?.url ?? "?";
  if (error.response) {
    // Request was made but server responded with something
    // other than 2xx
    console.error("Request Failed:", method, url, error.response.status);
    if (
      error.response.data?.statusCode === 401 &&
      [
        messages.invalidCredentials,
        messages.missingAuthentication,
        messages.expiredToken,
      ].includes(error.response.data.message)
    ) {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(PREVIOUS_USER_KEY);
      window.localStorage.removeItem(USER_KEY);
      window.location.reload();
    }
  } else {
    // No response (network, CORS, timeout, or the request couldn't be built).
    console.error("Request Failed:", method, url, error?.message);
  }

  const data = error?.response?.data;
  return Promise.reject({
    errMsg: !error?.response ? "Network Issue!" : errorMessage(data),
    status: error?.response?.status || "not status",
    // Additive: a few endpoints (pixel audience resume) answer one status with
    // several reasons, told apart by `code` and extra fields on the body.
    code:
      data && typeof data === "object" && typeof data.code === "string"
        ? data.code
        : undefined,
    data,
    // Structured field errors (e.g. `[{ path, message }]`) and Retry-After,
    // passed through for callers that map them to form fields / retry hints.
    errors: Array.isArray(error?.response?.data?.errors)
      ? error.response.data.errors
      : undefined,
    retryAfter: error?.response?.headers?.["retry-after"],
    // Existing row id on a duplicate-create 409 (onboarding distribution).
    connectionId: error?.response?.data?.connectionId,
    // Onboarding audiences (FE contract §7): error code + offending ids, and
    // the existing copy's id on a duplicate from-existing 409.
    fileIds: error?.response?.data?.fileIds,
    connectionIds: error?.response?.data?.connectionIds,
    onboardingAudienceId: error?.response?.data?.onboardingAudienceId,
  });
};

client.interceptors.response.use(onSuccess, onError);

export function addRequestInterceptors({
  advertiserId,
}: {
  advertiserId: number;
}) {
  client.interceptors.request.use((config) => {
    if (config.method === "get") {
      if (!config.url?.includes("advertiserId=")) {
        config.params = { ...config.params, advertiserId };
      }
    }
    return config;
  });
}

export default client;
